import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, ExternalLink, Pencil, History, Search, Plus, Loader2, FileText, LayoutTemplate, Type, Puzzle } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';
import { PageHistoryDialog } from '@/components/PageHistoryDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/App';

const KIND = {
  text: { label: 'Texte modifiable', icon: FileText, cls: 'bg-sky-50 text-sky-700' },
  hero: { label: 'Titre modifiable', icon: Type, cls: 'bg-indigo-50 text-indigo-700' },
  module: { label: 'Module dédié', icon: Puzzle, cls: 'bg-amber-50 text-amber-700' },
  custom: { label: 'Page libre', icon: LayoutTemplate, cls: 'bg-emerald-50 text-emerald-700' },
};
const GROUPS = ['Pages principales', 'Contenus', 'Informations légales', 'Pages personnalisées'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const PageRow = ({ page, onEdit, onHistory }) => {
  const k = KIND[page.kind] || KIND.module;
  return (
    <li className="group flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 hover:bg-gray-50/70 transition-colors" data-testid={`site-page-row-${page.key}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${k.cls}`}><k.icon size={16} /></span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{page.name}{!page.published && <span className="ml-2 text-[10px] font-medium text-gray-400 uppercase">brouillon</span>}</p>
          <p className="text-xs text-gray-400 font-mono truncate">{page.path}</p>
        </div>
      </div>
      <div className="flex items-center gap-4 sm:w-[300px] text-xs text-gray-500">
        <span className={`px-2 py-0.5 rounded-full font-medium ${k.cls}`}>{k.label}</span>
        <span className="truncate" title={page.updated_by_name || ''} data-testid={`site-page-updated-${page.key}`}>
          {fmtDate(page.updated_at)}{page.updated_by_name ? ` · ${page.updated_by_name}` : ''}
        </span>
      </div>
      <div className="flex items-center gap-1.5 sm:justify-end">
        <Button size="sm" variant="ghost" asChild data-testid={`site-page-view-${page.key}`}>
          <a href={page.path} target="_blank" rel="noopener noreferrer" title="Voir la page"><ExternalLink size={15} /></a>
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onHistory(page)} disabled={!page.has_history} title={page.has_history ? 'Historique' : "L'historique se trouve sur chaque contenu du module"} data-testid={`site-page-history-${page.key}`}>
          <History size={15} />{page.versions_count > 0 && <span className="ml-1 text-[11px]">{page.versions_count}</span>}
        </Button>
        <Button size="sm" className="bg-[#35AFE4] hover:bg-[#1f9fd6] text-white" onClick={() => onEdit(page)} data-testid={`site-page-edit-${page.key}`}>
          <Pencil size={14} /><span className="ml-1.5">Modifier</span>
        </Button>
      </div>
    </li>
  );
};

export default function SiteOverviewPage() {
  const navigate = useNavigate();
  const [pages, setPages] = useState(null);
  const [q, setQ] = useState('');
  const [historyPage, setHistoryPage] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/site-pages`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setPages(r.data))
      .catch(() => { setPages([]); toast.error('Impossible de charger les pages'); });
  }, []);
  useEffect(load, [load]);

  const grouped = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (pages || []).filter(p => !term || p.name.toLowerCase().includes(term) || p.path.toLowerCase().includes(term));
    return GROUPS.map(g => ({ g, items: list.filter(p => p.group === g) })).filter(x => x.items.length);
  }, [pages, q]);

  const createPage = () => {
    const title = window.prompt('Titre de la nouvelle page :');
    if (!title) return;
    const slug = title.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    navigate(`/admin/page-builder/${slug}?title=${encodeURIComponent(title)}`);
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-6xl" data-testid="site-pages-list">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              <Globe size={20} className="text-[#35AFE4]" /> Toutes les pages du site
            </h1>
            <p className="text-sm text-gray-500 mt-1">Chaque page est modifiable ; les textes gardent un historique pour revenir en arrière.</p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher une page…" className="pl-9 w-full sm:w-56" data-testid="site-pages-search" />
            </div>
            <Button onClick={createPage} variant="outline" className="flex-shrink-0" data-testid="site-pages-create-btn"><Plus size={15} /><span className="ml-1.5 hidden sm:inline">Nouvelle page</span></Button>
          </div>
        </div>

        {pages === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : grouped.length === 0 ? (
          <p className="text-sm text-gray-500 py-12 text-center" data-testid="site-pages-empty">Aucune page ne correspond à « {q} ».</p>
        ) : (
          <div className="space-y-6">
            {grouped.map(({ g, items }) => (
              <section key={g} data-testid={`site-pages-group-${g}`}>
                <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2 px-1">{g} · {items.length}</h2>
                <ul className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
                  {items.map(p => <PageRow key={p.key} page={p} onEdit={pg => navigate(pg.editor)} onHistory={setHistoryPage} />)}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
      <PageHistoryDialog page={historyPage} open={!!historyPage} onOpenChange={o => !o && setHistoryPage(null)} onRestored={load} />
    </AdminLayout>
  );
}
